import { createContext, useContext, useState, useEffect } from "react";

const FilterContext = createContext();

export const useFilters = () => {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error("useFilters must be used within a FilterProvider");
  }
  return context;
};

const DEFAULT_RADIUS_METERS = 25000; // 25km — sensible default for a city

export const FilterProvider = ({ children }) => {
  const [filters, setFilters] = useState({
    searchTerm: "",
    category: "",
    categoryName: "",

    // ============= LOCATION FILTERING (NEW SHAPE) =============
    // userCoordinates: [lng, lat] — the center point for radius search.
    //   Either pulled from the user's profile, browser geolocation, or
    //   a manual location they picked on the browse page.
    // userLocationLabel: human-readable string for display ("Sandton, JHB, ZA")
    // radius: meters
    // useLocationFilter: master toggle. When false, fetches all listings.
    userCoordinates: null,
    userLocationLabel: null,
    radius: DEFAULT_RADIUS_METERS,
    useLocationFilter: false,

    sortBy: "newest",
  });

  const [activeFilters, setActiveFilters] = useState([]);

  useEffect(() => {
    const active = [];

    if (filters.searchTerm) {
      active.push({
        type: "searchTerm",
        value: filters.searchTerm,
        label: `Search: ${filters.searchTerm}`,
      });
    }

    if (filters.category && filters.categoryName) {
      active.push({
        type: "category",
        value: filters.category,
        label: `Category: ${filters.categoryName}`,
      });
    }

    if (filters.useLocationFilter && filters.userCoordinates) {
      const km = Math.round(filters.radius / 1000);
      const where = filters.userLocationLabel || "your location";
      active.push({
        type: "location",
        value: filters.userCoordinates,
        label: `Within ${km}km of ${where}`,
      });
    }

    setActiveFilters(active);
  }, [filters]);

  const updateFilter = (key, value, extraData = {}) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      ...extraData,
    }));
  };

  /**
   * Set the location filter from a coords + label pair.
   * This is what the picker calls when the user picks "use my location" or
   * an autocomplete result on the browse page.
   */
  const setLocationFilter = (coordinates, label) => {
    setFilters((prev) => ({
      ...prev,
      userCoordinates: coordinates,
      userLocationLabel: label,
      useLocationFilter: true,
    }));
  };

  const clearLocationFilter = () => {
    setFilters((prev) => ({
      ...prev,
      userCoordinates: null,
      userLocationLabel: null,
      useLocationFilter: false,
    }));
  };

  const setRadius = (radiusInMeters) => {
    setFilters((prev) => ({ ...prev, radius: radiusInMeters }));
  };

  const removeFilter = (type) => {
    const updates = { ...filters };

    switch (type) {
      case "searchTerm":
        updates.searchTerm = "";
        break;
      case "category":
        updates.category = "";
        updates.categoryName = "";
        break;
      case "location":
        updates.userCoordinates = null;
        updates.userLocationLabel = null;
        updates.useLocationFilter = false;
        break;
      default:
        break;
    }

    setFilters(updates);
  };

  const clearAllFilters = () => {
    setFilters({
      searchTerm: "",
      category: "",
      categoryName: "",
      userCoordinates: null,
      userLocationLabel: null,
      radius: DEFAULT_RADIUS_METERS,
      useLocationFilter: false,
      sortBy: "newest",
    });
  };

  return (
    <FilterContext.Provider
      value={{
        filters,
        activeFilters,
        updateFilter,
        setLocationFilter,
        clearLocationFilter,
        setRadius,
        removeFilter,
        clearAllFilters,
      }}
    >
      {children}
    </FilterContext.Provider>
  );
};